import React from "react";
import { Box, Container, Typography, Link, Chip } from "@mui/material";

const bills = [
  {
    id: "HB 215",
    title: "Appoquinimink School District Budget Accountability Act",
    summary:
      "Requires independent audits for school districts over $50M and mandates public referendum before any operating tax increase exceeding 5% per year.",
    status: "Active - In Committee",
    statusColor: "#E8F5E9",
    statusTextColor: "#2E7D32",
    position: "Strongly Supports",
    redirect_link: "https://legis.delaware.gov/",
  },
  {
    id: "SB 88",
    title: "Property Tax Reassessment Transparency Act",
    summary:
      "Requires 90-day notice before property reassessments and public hearings before tax liability changes take effect.",
    status: "Under Review",
    statusColor: "#FFF3E0",
    statusTextColor: "#E65100",
    position: "Supports - Reviewing Details",
    redirect_link: "https://legis.delaware.gov/",
  },
  {
    id: "HB 142",
    title: "Small Business Protection Act",
    summary:
      "Limits new state regulations during budget shortfalls without legislative approval, protecting Middletown small businesses from surprise compliance costs.",
    status: "Active",
    statusColor: "#E8F5E9",
    statusTextColor: "#2E7D32",
    position: "Supports",
    redirect_link: "https://legis.delaware.gov/",
  },
  {
    id: "HB 245",
    title: "School Property Tax Cap Act",
    summary:
      "Removes the up to 10% school property tax increase currently allowed after reassessment years, protecting homeowners from sudden tax hikes.",
    status: "Tabled in Committee",
    statusColor: "#F5F5F5",
    statusTextColor: "#616161",
    position: "Supports",
    redirect_link: "https://legis.delaware.gov/BillDetail?LegislationId=142682",
  },
];

const LegislationList = () => {
  return (
    <Box
      className="legislation-section-root"
      sx={{ bgcolor: "#FFF", py: { xs: 6, md: 8 }, width: "100%" }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              color: "#C59324",
              fontWeight: 700,
              letterSpacing: "0.2em",
              fontSize: "0.85rem",
              textTransform: "uppercase",
              mb: 2,
            }}
          >
            <Box
              component="span"
              sx={{ width: 30, height: 1.5, bgcolor: "#C59324", mr: 1.5 }}
            />
            District 8 Legislation
          </Typography>

          <Typography
            sx={{
              mb: "8px",
              color: "#333",
              fontWeight: 700,
              fontFamily: "'Playfair Display', serif",
              mt: 1,
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
              },
            }}
          >
            Bills That{" "}
            <Box
              component="span"
              sx={{
                color: "#C59324",
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontWeight: 500,
              }}
            >
              Impact Your Life
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "#5D6785",
              fontSize: "0.9rem",
              lineHeight: 1.6,
              maxWidth: "550px",
            }}
          >
            I believe in transparency. Here's what's actually moving through
            Dover – with plain-English summaries and Gary's position on each
            bill.
          </Typography>
        </Box>

        {/* Bill Cards */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {bills.map((bill, index) => (
            <Box
              key={index}
              sx={{
                bgcolor: "#FFF",
                p: { xs: 2, md: 3 },
                borderRadius: "6px",
                border: "1px solid rgba(0,0,0,0.08)",
                transition: "all 0.2s ease",
                "&:hover": { borderColor: "#C59324" },
              }}
            >
              {/* Top row: Bill ID + Title + View link */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "flex-start" },
                  gap: { xs: 1, sm: "12px" },
                  mb: 1,
                }}
              >
                {/* Bill ID */}
                <Box
                  sx={{
                    color: "#C59324",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    minWidth: "fit-content",
                    background: "#faf3e0",
                    padding: "5px 10px",
                    borderRadius: "5px",
                    flexShrink: 0,
                  }}
                >
                  {bill.id}
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    color: "#051328",
                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                    fontWeight: 800,
                    flex: 1,
                    lineHeight: 1.4,
                  }}
                >
                  {bill.title}
                </Typography>

                {/* View link */}
                <Link
                  href={bill.redirect_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#C59324",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    alignSelf: { xs: "flex-end", sm: "flex-start" },
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  View →
                </Link>
              </Box>

              {/* Summary */}
              <Typography
                sx={{
                  color: "#5D6785",
                  fontSize: "0.85rem",
                  lineHeight: 1.5,
                  mb: 1.5,
                  pl: { xs: 0, md: "0px" },
                }}
              >
                {bill.summary}
              </Typography>

              {/* Status + Position — left aligned, one line */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label={bill.status}
                  sx={{
                    bgcolor: bill.statusColor,
                    color: bill.statusTextColor,
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    height: "20px",
                    borderRadius: "4px",
                  }}
                />
                <Typography sx={{ fontSize: "0.75rem", color: "#051328" }}>
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    Gary's Position:{" "}
                  </Box>
                  <Box component="span" sx={{ color: "#5D6785" }}>
                    {bill.position}
                  </Box>
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default LegislationList;
